import React from 'react';
import { Box, LinearProgress } from '@mui/material';

/**
 * LoadingOverlay — the "out of focus" loading effect.
 *
 * Keeps existing content visible but dimmed + softly blurred + non-interactive,
 * with a slim top progress bar, while data refreshes. This is the preferred
 * pattern for REFRESHES / refetches (content is already on screen) — it avoids
 * the jarring content→skeleton→content flash and preserves the user's context.
 *
 * Use a skeleton (LoadingSkeleton) only for the VERY FIRST paint, when there is
 * nothing to dim yet. After that, wrap the content in <LoadingOverlay loading>.
 *
 * Honors prefers-reduced-motion globally (the theme neutralizes the transition).
 *
 * @param {boolean} loading - dim + blur the content while true
 * @param {number} blur - blur radius in px (default 2)
 * @param {number} dim - opacity while loading (default 0.55)
 * @param {number|string} radius - border radius for the progress bar corners
 */
const LoadingOverlay = ({ loading = false, children, blur = 2, dim = 0.55, radius = 0 }) => (
  <Box sx={{ position: 'relative' }} aria-busy={loading || undefined}>
    {loading && (
      <LinearProgress
        aria-label="Loading"
        sx={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, height: 2,
          borderTopLeftRadius: radius, borderTopRightRadius: radius,
        }}
      />
    )}
    <Box
      sx={{
        transition: 'opacity .2s ease, filter .2s ease',
        ...(loading
          ? { opacity: dim, filter: `blur(${blur}px)`, pointerEvents: 'none', userSelect: 'none' }
          : null),
      }}
    >
      {children}
    </Box>
  </Box>
);

export default LoadingOverlay;
